import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import services from '../data/services';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import '../styles/pages/services-detail.css';

const DEMO_URL = 'https://calendar.app.google/6g62S4xtRa8eWipt5';

/* Wrap one phrase of a data-driven headline in <em> (the single serif accent). */
function accent(text, phrase) {
  const i = phrase ? text.indexOf(phrase) : -1;
  if (i < 0) return text;
  return <>{text.slice(0, i)}<em>{phrase}</em>{text.slice(i + phrase.length)}</>;
}

/* "Full-Stack Expertise - Blockchain + Web" -> ["Full-Stack Expertise", "Blockchain + Web"] */
function splitReason(text) {
  const i = text.indexOf(' - ');
  return i < 0 ? [text, ''] : [text.slice(0, i), text.slice(i + 3)];
}

const pad = (n) => String(n).padStart(2, '0');

/* Pointer-follow spotlight (CSS vars only). */
function trackSpotlight(e) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty('--mx', `${e.clientX - r.left}px`);
  el.style.setProperty('--my', `${e.clientY - r.top}px`);
}

const STACK_ICONS = { blockchain: 'fa-cube', tokenStandard: 'fa-code', security: 'fa-shield-halved' };

function ServiceDetail() {
 const { serviceId } = useParams();
 const service = services[serviceId];

 useEffect(() => {
 window.scrollTo(0, 0);

 // Update page title and meta tags
 if (service) {
 document.title = `${service.title} - Moneli Automation Inc.`;

 // Update meta description
 let metaDescription = document.querySelector('meta[name="description"]');
 if (!metaDescription) {
 metaDescription = document.createElement('meta');
 metaDescription.name = 'description';
 document.head.appendChild(metaDescription);
 }
 metaDescription.content = service.hero.description;

 // Add Schema.org structured data for SEO
 const structuredData = {
 "@context": "https://schema.org",
 "@type": "Service",
 "serviceType": service.title,
 "provider": {
 "@type": "Organization",
 "name": "Moneli Automation Inc.",
 "url": "https://moneliautomation.com",
 "logo": "https://moneliautomation.com/logo.png"
 },
 "description": service.hero.description,
 "offers": service.packages.map(pkg => ({
 "@type": "Offer",
 "name": `${pkg.tier} Package`,
 "price": pkg.price.replace(/[^0-9]/g, ''),
 "priceCurrency": "USD",
 "description": pkg.features.join(', '),
 "availability": "https://schema.org/InStock"
 })),
 "areaServed": {
 "@type": "Country",
 "name": "Global"
 },
 "aggregateRating": {
 "@type": "AggregateRating",
 "ratingValue": "4.9",
 "reviewCount": "15"
 }
 };

 // Add or update schema script
 let schemaScript = document.getElementById('service-schema');
 if (!schemaScript) {
 schemaScript = document.createElement('script');
 schemaScript.id = 'service-schema';
 schemaScript.type = 'application/ld+json';
 document.head.appendChild(schemaScript);
 }
 schemaScript.textContent = JSON.stringify(structuredData);

 // Track page view (placeholder for analytics)
 if (window.gtag) {
 window.gtag('event', 'page_view', {
 page_title: service.title,
 page_location: window.location.href,
 page_path: `/services/${serviceId}`,
 service_type: service.title
 });
 }

 // Custom event for service view
 if (window.gtag) {
 window.gtag('event', 'view_service', {
 service_id: serviceId,
 service_name: service.title,
 service_category: 'Blockchain & Cryptocurrency'
 });
 }
 }

 // Cleanup function
 return () => {
 document.title = 'Moneli Automation Inc.';
 };
 }, [service, serviceId]);

 if (!service) {
 return <Navigate to="/" replace />;
 }

 const { hero, techStack, roi } = service;
 const orbit = techStack.tokenStandard.join(' • ') + ' • ' + techStack.tokenStandard.join(' • ') + ' • ';

 return (
 <div className="svd-page">
 {/* HERO */}
 <PageHero
 variant="dark"
 className="svd-hero"
 crumbs={[{ to: '/', label: 'Home' }, { to: '/services', label: 'Services' }, { label: service.shortTitle }]}
 eyebrow={hero.subtitle}
 title={accent(hero.title, 'Digital Currency')}
 lead={hero.description}
 actions={<>
 <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg" data-magnetic>
 <i className="fa-solid fa-calendar-check" aria-hidden="true" /> Book a Demo
 </a>
 <a href="#pricing" className="btn btn-outline-light btn-lg">
 View Pricing <i className="fa-solid fa-arrow-down" aria-hidden="true" />
 </a>
 </>}
 >
 <div className="svd-token" aria-hidden="true">
 <div className="svd-token__glow" />
 <div className="svd-token__coin" data-parallax="-0.06">
 <svg viewBox="0 0 200 200" className="svd-token__ring">
 <defs><path id="svd-ring-path" d="M100,100 m-82,0 a82,82 0 1,1 164,0 a82,82 0 1,1 -164,0" /></defs>
 <text><textPath href="#svd-ring-path">{orbit}</textPath></text>
 </svg>
 <span className="svd-token__face"><i className={`fa-solid ${service.icon}`} /></span>
 </div>
 {techStack.blockchain.map((b, i) => (
 <span key={b} className={`svd-token__chip svd-token__chip--${i + 1}`} data-parallax={['0.1', '-0.08', '0.05'][i]}>
 <i className="fa-solid fa-cube" /> {b}
 </span>
 ))}
 </div>
 </PageHero>

 {/* PERFECT FOR */}
 <section className="section section--light" aria-labelledby="svd-for-title">
 <div className="container">
 <SectionHeader
 index="01"
 eyebrow="Who it's for"
 title={<>Perfect For <em>Your Industry</em></>}
 lead={service.description}
 id="svd-for-title"
 />
 <ul className="svd-for" data-reveal-stagger="0.06">
 {service.perfectFor.map((item, idx) => (
 <li key={item.title} className="svd-for__item">
 <span className="svd-for__num">{pad(idx + 1)}</span>
 <h3 className="svd-for__title">{item.title}</h3>
 <p className="svd-for__text">{item.description}</p>
 <i className="fa-solid fa-arrow-right svd-for__arrow" aria-hidden="true" />
 </li>
 ))}
 </ul>
 </div>
 </section>

 {/* DEVELOPMENT PROCESS */}
 <section className="section section--dark grain" aria-labelledby="svd-process-title">
 <div className="container">
 <SectionHeader
 index="02"
 eyebrow="Development process"
 title={<>Development <em>Process</em></>}
 lead="From strategy to launch in 4-6 weeks"
 id="svd-process-title"
 />
 <div className="svd-timeline">
 <hr className="rule svd-timeline__rule" data-reveal-line />
 <ol className="svd-phases" data-reveal-stagger="0.12">
 {service.phases.map((phase) => (
 <li key={phase.number} className="svd-phase">
 <div className="svd-phase__head">
 <span className="svd-phase__num">{pad(phase.number)}</span>
 <span className="svd-phase__time">{phase.timeline}</span>
 </div>
 <h3 className="svd-phase__title">{phase.title}</h3>
 <ul className="svd-phase__list">
 {phase.items.map((item) => <li key={item}>{item}</li>)}
 </ul>
 </li>
 ))}
 </ol>
 </div>
 </div>
 </section>

 {/* KEY FEATURES */}
 <section className="section section--sand" aria-labelledby="svd-features-title">
 <div className="container">
 <SectionHeader
 index="03"
 eyebrow="Key features"
 title={<>Powerful <em>Features</em></>}
 align="center"
 id="svd-features-title"
 />
 <div className="grid-4 svd-features" data-reveal-stagger="0.07">
 {service.features.map((f, idx) => (
 <article key={f.title} className={`card card--feature svd-feature${idx === 1 ? ' card--note' : ''}`}>
 <span className={`icon-tile${idx === 1 ? ' icon-tile--ink' : ''}`}><i className={`fa-solid ${f.icon}`} aria-hidden="true" /></span>
 <h3 className="card__title">{f.title}</h3>
 <p className="card__text">{f.description}</p>
 </article>
 ))}
 </div>
 </div>
 </section>

 {/* TECHNICAL STACK */}
 <section className="section section--light" aria-labelledby="svd-stack-title">
 <div className="container">
 <SectionHeader
 index="04"
 eyebrow="Under the hood"
 title={<><em>Technical</em> Stack</>}
 lead={`Smart contracts in ${techStack.smartContracts}.`}
 id="svd-stack-title"
 />
 <div className="svd-bento" data-reveal-stagger="0.08">
 {[['blockchain', 'Blockchain'], ['tokenStandard', 'Token Standard'], ['security', 'Security']].map(([key, label]) => (
 <article key={key} className="card card--feature svd-bento__cell">
 <div className="svd-bento__head">
 <span className="icon-tile icon-tile--ink"><i className={`fa-solid ${STACK_ICONS[key]}`} aria-hidden="true" /></span>
 <h3 className="card__title">{label}</h3>
 </div>
 <ul className="ticks">
 {techStack[key].map((t) => <li key={t}>{t}</li>)}
 </ul>
 </article>
 ))}
 <article className="card card--feature svd-bento__cell svd-bento__cell--wide">
 <div className="svd-bento__head">
 <span className="icon-tile"><i className="fa-solid fa-plug" aria-hidden="true" /></span>
 <h3 className="card__title">API</h3>
 </div>
 <p className="card__text">{techStack.api}</p>
 </article>
 <article className="card card--feature svd-bento__cell svd-bento__cell--wide">
 <div className="svd-bento__head">
 <span className="icon-tile"><i className="fa-solid fa-puzzle-piece" aria-hidden="true" /></span>
 <h3 className="card__title">Integration</h3>
 </div>
 <p className="card__text">{techStack.integration}</p>
 </article>
 </div>
 </div>
 </section>

 {/* PRICING */}
 <section id="pricing" className="section section--dark grain svd-pricing" aria-labelledby="svd-pricing-title">
 <div className="container">
 <SectionHeader
 index="05"
 eyebrow="Packages"
 title={<>Transparent <em>Pricing</em></>}
 lead="Choose the package that fits your business needs"
 id="svd-pricing-title"
 />
 <div className="svd-plans" data-reveal-stagger="0.12">
 {service.packages.map((pkg) => (
 <article
 key={pkg.tier}
 className={`card card--dark svd-plan${pkg.featured ? ' svd-plan--featured' : ''}`}
 onPointerMove={trackSpotlight}
 >
 {pkg.featured && <span className="pill pill--note svd-plan__flag">Most Popular</span>}
 <header className="svd-plan__head">
 <span className="svd-plan__medal" aria-hidden="true">{pkg.icon}</span>
 <h3 className="svd-plan__tier">{pkg.tier} Package</h3>
 </header>
 <p className="svd-plan__price">{pkg.price}</p>
 <p className="svd-plan__best">Best for: {pkg.bestFor}</p>
 <ul className="ticks svd-plan__list">
 {pkg.features.map((f) => <li key={f}>{f}</li>)}
 </ul>
 <a
 href={DEMO_URL}
 target="_blank"
 rel="noopener noreferrer"
 className={`btn ${pkg.featured ? 'btn-primary' : 'btn-outline-light'} svd-plan__cta`}
 {...(pkg.featured ? { 'data-magnetic': '0.2' } : {})}
 >
 Get Started <i className="fa-solid fa-arrow-right" aria-hidden="true" />
 </a>
 </article>
 ))}
 </div>
 </div>
 </section>

 {/* ROI */}
 <section className="section section--sand" aria-labelledby="svd-roi-title">
 <div className="container">
 <SectionHeader
 index="06"
 eyebrow="Return on investment"
 title={<>Proven <em>ROI</em></>}
 lead="Investment that pays for itself"
 id="svd-roi-title"
 />
 <div className="grid-2 svd-roi">
 <article className="card card--feature card--flat svd-roi__card" data-reveal="left">
 <h3 className="card__title">{roi.costSavings.title}</h3>
 <div className="svd-compare">
 <div className="svd-compare__col">
 <p className="svd-compare__label">Traditional Payment Processing:</p>
 <dl className="svd-dl">
 <div><dt>Fees</dt><dd>{roi.costSavings.traditional.fees}</dd></div>
 <div><dt>Volume</dt><dd>{roi.costSavings.traditional.volume}</dd></div>
 <div><dt>Fees Paid</dt><dd className="svd-dl__neg">{roi.costSavings.traditional.feesPaid}</dd></div>
 </dl>
 </div>
 <span className="svd-compare__arrow" aria-hidden="true"><i className="fa-solid fa-arrow-right" /></span>
 <div className="svd-compare__col svd-compare__col--win">
 <p className="svd-compare__label">With Custom Token:</p>
 <dl className="svd-dl">
 <div><dt>Fees</dt><dd>{roi.costSavings.withToken.fees}</dd></div>
 <div><dt>Annual Savings</dt><dd className="svd-dl__pos">{roi.costSavings.withToken.savings}</dd></div>
 <div><dt>Investment Payback</dt><dd className="svd-dl__pos">{roi.costSavings.withToken.payback}</dd></div>
 </dl>
 </div>
 </div>
 </article>
 <article className="card card--feature card--flat svd-roi__card" data-reveal="right" data-delay="0.1">
 <h3 className="card__title">{roi.revenueIncrease.title}</h3>
 <ul className="svd-metrics">
 {roi.revenueIncrease.metrics.map((m) => (
 <li key={m.metric} className="svd-metrics__row">
 <span>{m.metric}</span>
 <strong>{m.value}</strong>
 </li>
 ))}
 </ul>
 </article>
 </div>
 </div>
 </section>

 {/* SUCCESS STORIES */}
 <section className="section section--light" aria-labelledby="svd-stories-title">
 <div className="container">
 <SectionHeader
 index="07"
 eyebrow="Client stories"
 title={<>Client <em>Success Stories</em></>}
 id="svd-stories-title"
 />
 <div className="grid-3 svd-stories" data-reveal-stagger="0.1">
 {service.successStories.map((story) => (
 <figure key={story.company} className="card svd-story">
 <blockquote className="svd-story__text"><p>&ldquo;{story.quote}&rdquo;</p></blockquote>
 <figcaption className="svd-story__by">— {story.company}</figcaption>
 </figure>
 ))}
 </div>
 </div>
 </section>

 {/* WHY CHOOSE US */}
 <section className="section section--dark grain" aria-labelledby="svd-why-title">
 <div className="container">
 <SectionHeader
 index="08"
 eyebrow="Why Moneli"
 title={<>Why Choose <em>Moneli</em></>}
 id="svd-why-title"
 />
 <ul className="svd-why" data-reveal-stagger="0.08">
 {service.whyChooseUs.map((r) => {
 const [title, text] = splitReason(r.text);
 return (
 <li key={r.text} className="svd-why__item">
 <span className="svd-why__icon"><i className={`fa-solid ${r.icon}`} aria-hidden="true" /></span>
 <div>
 <h3 className="svd-why__title">{title}</h3>
 {text && <p className="svd-why__text">{text}</p>}
 </div>
 </li>
 );
 })}
 </ul>
 </div>
 </section>

 {/* ADD-ONS */}
 <section className="section section--sand" aria-labelledby="svd-addons-title">
 <div className="container">
 <div className="svd-addons">
 <SectionHeader
 align="stack"
 index="09"
 eyebrow="Extras"
 title={<><em>Add-On</em> Services</>}
 lead="Enhance your token ecosystem"
 id="svd-addons-title"
 />
 <ul className="svd-menu" data-reveal-stagger="0.06">
 {service.addOns.map((a) => (
 <li key={a.service} className="svd-menu__row">
 <span className="svd-menu__name">{a.service}</span>
 <span className="svd-menu__dots" aria-hidden="true" />
 <strong className="svd-menu__price">{a.price}</strong>
 </li>
 ))}
 </ul>
 </div>
 </div>
 </section>

 {/* FAQ + CLOSING (light; the global footer follows with the dark CTA band) */}
 <section className="section section--light" aria-labelledby="svd-faq-title">
 <div className="container">
 <div className="svd-faq">
 <div>
 <SectionHeader
 align="stack"
 index="10"
 eyebrow="FAQ"
 title={<>Frequently Asked <em>Questions</em></>}
 id="svd-faq-title"
 />
 <div className="svd-faq__list" data-reveal-stagger="0.06">
 {service.faqs.map((faq, idx) => (
 <details key={faq.question} className="svd-qa" open={idx === 0}>
 <summary className="svd-qa__q">
 <span>{faq.question}</span>
 <span className="svd-qa__icon" aria-hidden="true" />
 </summary>
 <p className="svd-qa__a">{faq.answer}</p>
 </details>
 ))}
 </div>
 </div>

 <aside className="card card--note card--flat svd-close" data-reveal="right" data-delay="0.2" aria-labelledby="svd-close-title">
 <h2 className="svd-close__title" id="svd-close-title">Ready to Build Your Token Ecosystem?</h2>
 <p className="card__text">
 Book a free 30-minute consultation to discuss your business goals and how a custom cryptocurrency can transform your platform.
 </p>
 <div className="svd-close__actions">
 <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="btn btn-ink btn-lg" data-magnetic>
 <i className="fa-solid fa-calendar-check" aria-hidden="true" /> Book a Demo
 </a>
 <Link to="/contact" className="btn btn-ghost">
 <i className="fa-solid fa-envelope" aria-hidden="true" /> Contact Us
 </Link>
 </div>
 </aside>
 </div>
 </div>
 </section>
 </div>
 );
}

export default ServiceDetail;
